import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  Fade,
  Flex,
  HStack,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { H2 } from "../../../../components/text/headings";
import { useEffect, useRef, useState } from "react";
import { useGetTotalEmissions } from "../../../../hooks/query/emissions-insight/emissions-insights-query";
import LocalSpinner from "../../../../components/LocalSpinner";
import { CaretDown, ChartDonut, Check, ListBullets } from "@phosphor-icons/react";
import { useGetOrganizationStructure } from "../../../../hooks/query";
import SummaryCards from "./SummaryCards";
import KpiCharts from "./KpiCharts";
import { useSearchParams } from "react-router-dom";
import ScopeOneDetail from "./details/ScopeOneDetail";
import ScopeTwoDetail from "./details/ScopeTwoDetail";

const FootPrint = () => {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const detailView = searchParams.get("detailView");

  // Initialize from URL params
  const initialYear = Number(searchParams.get("year")) || new Date().getFullYear();
  const initialDimension = (() => {
    const dimParam = searchParams.get("dimension");
    if (!dimParam) return {};
    return dimParam.split("&").reduce((acc, pair) => {
      const [key, values] = pair.split("=");
      acc[key] = values ? values.split(",") : [];
      return acc;
    }, {});
  })();

  // States
  const [year, setYear] = useState(initialYear);
  const [selectedViewType, setSelectedViewType] = useState("summary");
  const [dimension, setDimension] = useState(initialDimension);

  // Update URL params when year or dimension changes
  useEffect(() => {
    const params = searchParams;
    params.set("year", year);
    if (Object.keys(dimension).length > 0) {
      params.set(
        "dimension",
        Object.entries(dimension)
          .filter(([_, ids]) => ids.length > 0)
          .map(([key, ids]) => `${key}=${ids.join(",")}`)
          .join("&")
      );
    }
    setSearchParams(params);
  }, [year, dimension, searchParams, setSearchParams]);

  const getDimensionParams = () => {
    return Object.entries(dimension)
      .filter(([_, ids]) => ids.length > 0) // Filter out dimensions with no selected items
      .map(([key, ids]) => `${key}=${ids.join(",")}`) // Format each dimension as key=value1,value2
      .join("&"); // Join all dimensions with '&'
  };

  const handleEmissionsError = (error, year) => {
    if (error?.response?.status === 404) {
      toast({
        title: "Report not available for year " + year,
        description: "The emissions report for this year is not available.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
    } else {
      toast({
        title: "Error fetching current year emissions data",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const { data: currentYearData, isFetching: isFetchingCurrentYear } = useGetTotalEmissions(
    year,
    getDimensionParams(),
    {
      onError: (error) => handleEmissionsError(error, year),
      cacheTime: 60, // 1 minute
    }
  );

  const { data: previousYearData, isFetching: isFetchingPreviousYear } = useGetTotalEmissions(
    year - 1,
    getDimensionParams(),
    {
      onError: (error) => handleEmissionsError(error, year - 1),
      cacheTime: 60, // 1 minute
    }
  );

  return (
    <Flex direction="column" gap={4}>
      <Flex alignSelf="end" gap={2}>
        <DimensionDropdown selected={dimension} setSelected={setDimension} />
        <YearDropdown setYear={setYear} selectedYear={year} />
      </Flex>

      {detailView === "scopeOne" && (
        <ScopeOneDetail
          currentYearData={currentYearData}
          previousYearData={previousYearData}
          isFetchingCurrentYear={isFetchingCurrentYear}
          isFetchingPreviousYear={isFetchingPreviousYear}
        />
      )}
      {detailView === "scopeTwo" && (
        <ScopeTwoDetail
          currentYearData={currentYearData}
          previousYearData={previousYearData}
          isFetchingCurrentYear={isFetchingCurrentYear}
          isFetchingPreviousYear={isFetchingPreviousYear}
        />
      )}

      {!detailView && (
        <>
          <Flex width="fit-content" alignSelf="flex-end" mt={2}>
            <ToggleButton
              options={[
                { label: "Summary", value: "summary", icon: <ListBullets size={18} /> },
                { label: "Visual", value: "visual", icon: <ChartDonut size={18} /> },
              ]}
              selected={selectedViewType}
              setSelected={setSelectedViewType}
            />
          </Flex>
          <H2>Total Emissions</H2>
          <Fade in={selectedViewType === "summary"} unmountOnExit>
            <SummaryCards
              currentYearData={currentYearData}
              previousYearData={previousYearData}
              isFetchingCurrentYear={isFetchingCurrentYear}
              isFetchingPreviousYear={isFetchingPreviousYear}
            />
          </Fade>
          <Fade in={selectedViewType === "visual"} unmountOnExit>
            <KpiCharts
              currentYearData={currentYearData}
              previousYearData={previousYearData}
              isFetchingCurrentYear={isFetchingCurrentYear}
              isFetchingPreviousYear={isFetchingPreviousYear}
              selectedYear={year}
            />
          </Fade>
        </>
      )}
    </Flex>
  );
};

const ToggleButton = ({
  options,
  selected,
  setSelected,
  colorOn = "white",
  colorOff = "gray.100",
  colorHover = "white",
}) => {
  return (
    <ButtonGroup isAttached width="100%">
      {options.map((option) => (
        <Tooltip label={`Change view type to ${option.value}`} key={option.value}>
          <Button
            px={8}
            minW="100px"
            onClick={() => setSelected(option.value)}
            bg={selected === option.value ? colorOn : colorOff}
            _hover={{ bg: selected === option.value ? colorOn : colorHover }}
            flex="1"
            rightIcon={<Box fontWeight={selected === option.value ? "bold" : "normal"}>{option.icon}</Box>}
            fontWeight={selected === option.value ? "bold" : "normal"}
            border="solid 1px"
            borderRadius="lg"
            borderColor={selected === option.value ? "gray.300" : "gray.200"}
            boxShadow={selected === option.value ? "sm" : "none"}
          >
            {option.label || option.value}
          </Button>
        </Tooltip>
      ))}
    </ButtonGroup>
  );
};

export const YearDropdown = ({ setYear, selectedYear }) => {
  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  return (
    <Flex>
      <Menu>
        <MenuButton as={Button} variant="outline" rightIcon={<CaretDown size={16} />}>
          {selectedYear || "Select year"}
        </MenuButton>
        <MenuList>
          {years.map((year) => (
            <MenuItem key={year} onClick={() => setYear(year)} fontWeight={selectedYear === year ? "bold" : "normal"}>
              {year}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </Flex>
  );
};

const DimensionDropdown = ({ selected, setSelected }) => {
  /**
   * Staging have a very large number of items in each dimension over 1.7k which makes the page to hang.
   * So we are limiting the number of items displayed per dimension to 10 by adidng pagination.
   * @type {number}
   */
  const PAGE_SIZE = 10;

  const toast = useToast();

  const [search, setSearch] = useState("");
  const [inputValue, setInputValue] = useState(search);
  const [pageByDimension, setPageByDimension] = useState({});

  const selectedCount = Object.values(selected).reduce((acc, arr) => acc + (arr?.length || 0), 0);

  const { isLoading, data: organizationStructure } = useGetOrganizationStructure({
    cacheTime: 60 * 1000 * 5, // Cache for 5 minute
    onError: (error) => {
      console.error("Error fetching organization structure:", error);
      toast({
        title: "Error fetching organization structure",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });
  const dimensionsStructure = organizationStructure?.dimensions || {};

  const toggleItem = (dimensionKey, itemId) => {
    setSelected((prev) => {
      const current = prev[dimensionKey] || [];
      const exists = current.includes(itemId);
      return {
        ...prev,
        [dimensionKey]: exists ? current.filter((id) => id !== itemId) : [...current, itemId],
      };
    });
  };

  const handleClearAll = () => {
    const cleared = {};
    Object.keys(dimensionsStructure).forEach((key) => {
      cleared[key] = [];
    });
    setSelected(cleared);
  };

  const handlePageChange = (dimensionKey, newPage) => {
    setPageByDimension((prev) => ({
      ...prev,
      [dimensionKey]: newPage,
    }));
  };

  // Wait for user to stop typing before updating search
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearch(inputValue);
    }, 1000); // debounce

    return () => clearTimeout(handler);
  }, [inputValue, setSearch]);

  return (
    <Menu closeOnSelect={false}>
      {isLoading ? (
        <LocalSpinner />
      ) : (
        <MenuButton as={Button} variant="outline" rightIcon={<CaretDown size={16} />}>
          {selectedCount > 0 ? `Dimension (${selectedCount} selected)` : "Dimension"}
        </MenuButton>
      )}

      <MenuList maxHeight="400px" overflowY="auto">
        <Box px={3} py={2}>
          <Input
            type="text"
            placeholder="Search..."
            value={inputValue}
            // onChange={e => setSearch(e.target.value)}
            onChange={(e) => setInputValue(e.target.value)}
            w="100%"
            p="4px"
            size="sm"
            border="none"
          />
        </Box>
        {selectedCount > 0 && (
          <MenuItem color="status.red" fontSize="body.xxs" px={3} py={2} onClick={handleClearAll}>
            Clear All
          </MenuItem>
        )}
        {Object.entries(dimensionsStructure).map(([dimensionKey, dimension]) => {
          if (!dimension) return null;

          // Filter items by search
          const filteredItems = dimension.items.filter((item) =>
            item.name.toLowerCase().includes(search.toLowerCase())
          );
          const page = pageByDimension[dimensionKey] || 0;
          const totalPages = Math.ceil(filteredItems.length / PAGE_SIZE);

          const paginatedItems = filteredItems.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

          return (
            <Box key={dimensionKey}>
              <Box px={3} pt={2} pb={1} fontWeight="bold">
                {dimension.name.toUpperCase()}
              </Box>
              {filteredItems.length === 0 ? (
                <MenuItem disabled>No items</MenuItem>
              ) : (
                <>
                  {paginatedItems.map((item) => (
                    <MenuItem key={item.id} onClick={() => toggleItem(dimensionKey, item.id)}>
                      <HStack justifyContent="space-between" w="full">
                        <Text fontWeight={selected[dimensionKey]?.includes(item.id) ? "bold" : "normal"}>
                          {item.name}
                        </Text>
                        {selected[dimensionKey]?.includes(item.id) ? (
                          <Check size={16} weight="bold" color="#82D909" />
                        ) : null}
                      </HStack>
                    </MenuItem>
                  ))}
                  {totalPages > 1 && (
                    <HStack px={3} py={2} justifyContent="space-between">
                      <Button
                        size="xs"
                        onClick={() => handlePageChange(dimensionKey, page - 1)}
                        isDisabled={page === 0}
                      >
                        Prev
                      </Button>
                      <Text fontSize="xs">
                        Page {page + 1} of {totalPages}
                      </Text>
                      <Button
                        size="xs"
                        onClick={() => handlePageChange(dimensionKey, page + 1)}
                        isDisabled={page + 1 >= totalPages}
                      >
                        Next
                      </Button>
                    </HStack>
                  )}
                </>
              )}
              <Divider my={1} />
            </Box>
          );
        })}
      </MenuList>
    </Menu>
  );
};

export default FootPrint;
